// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { ExternalLink, Images } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface ScreenshotHeroProps {
  urls?: string[];
  appName: string;
  slug: string;
  appUrl?: string | null;
}

// Diagonal-hatch placeholder body — aria-hidden since it carries no content.
function HatchBody({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden
      className={cn('h-full w-full flex items-center justify-center', className)}
      style={{
        backgroundColor: 'hsl(var(--muted))',
        backgroundImage:
          'repeating-linear-gradient(45deg, transparent, transparent 6px, hsl(var(--border) / 0.5) 6px, hsl(var(--border) / 0.5) 7px)',
      }}
    >
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[hsl(var(--text-tertiary))]">
        Screenshot
      </span>
    </div>
  );
}

// Browser-chrome bar — the three dot indicators at the top of the frame.
function ChromeBar(): React.JSX.Element {
  return (
    <div className="h-8 shrink-0 flex items-center px-3.5 border-b border-border bg-muted/40">
      <span className="flex gap-1.5">
        <span className="w-2 h-2 rounded-full bg-border" aria-hidden />
        <span className="w-2 h-2 rounded-full bg-border" aria-hidden />
        <span className="w-2 h-2 rounded-full bg-border" aria-hidden />
      </span>
    </div>
  );
}

// A screenshot image that falls back to the hatch body on load error.
function ScreenshotTile({ url, alt }: { url: string; alt: string }): React.JSX.Element {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);

  if (failed) return <HatchBody />;

  return (
    <img
      className="h-full w-full bg-muted/40 object-contain"
      loading="lazy"
      src={url}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
}

function ScreenshotLink({
  url,
  alt,
  appUrl,
  nameLabel,
}: {
  url: string;
  alt: string;
  appUrl?: string | null;
  nameLabel: string;
}): React.JSX.Element {
  const image = <ScreenshotTile url={url} alt={alt} />;
  if (!appUrl) return image;

  return (
    <a
      href={appUrl}
      target="_blank"
      rel="noreferrer"
      aria-label={`Open ${nameLabel}`}
      title={`Open ${nameLabel}`}
      className="group relative block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {image}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/0 transition-colors group-hover:bg-background/10 group-focus-visible:bg-background/10"
      >
        <span className="flex size-9 items-center justify-center rounded-full border border-border bg-background/75 text-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-80 group-focus-visible:opacity-90">
          <ExternalLink className="size-4" aria-hidden />
        </span>
      </span>
    </a>
  );
}

/**
 * Single hero screenshot for the app-detail header zone (docs/28 §Imagery): at
 * most one screenshot renders inline (the first of `screenshot_urls`); the rest
 * sit behind a "+N more" lightbox. With no screenshots the component renders
 * nothing — no placeholder frames.
 */
export function ScreenshotHero({
  urls,
  appName,
  slug,
  appUrl,
}: ScreenshotHeroProps): React.JSX.Element | null {
  const shots = urls ?? [];
  const nameLabel = appName.trim() || slug;
  const [lightboxOpen, setLightboxOpen] = React.useState(false);

  const hero = shots[0];
  const more = shots.length - 1;

  // When a refetch leaves no extra screenshots, the controlled Dialog unmounts —
  // clear the flag so the lightbox doesn't reappear unbidden if extras return.
  React.useEffect(() => {
    if (more <= 0) setLightboxOpen(false);
  }, [more]);

  if (hero === undefined) return null;

  return (
    <figure
      className="relative flex h-full min-h-[180px] flex-col overflow-hidden rounded-gl-lg border border-border bg-card"
      data-testid="app-screenshot-hero"
    >
      <ChromeBar />
      <div className="relative min-h-0 flex-1">
        <ScreenshotLink
          url={hero}
          alt={`${nameLabel} screenshot 1`}
          appUrl={appUrl}
          nameLabel={nameLabel}
        />
        {more > 0 && (
          <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full border border-border bg-background/90 px-2.5 py-1 text-xs text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Images className="size-3.5" aria-hidden />+{more} more
              </button>
            </DialogTrigger>
            <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle>{nameLabel} screenshots</DialogTitle>
                <DialogDescription>{shots.length} captures of the deployed app.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4" data-testid="screenshot-lightbox">
                {shots.map((url, i) => (
                  <figure
                    key={`${url}-${i}`}
                    className="relative overflow-hidden rounded-gl-lg border border-border"
                  >
                    <ScreenshotTile url={url} alt={`${nameLabel} screenshot ${i + 1}`} />
                  </figure>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </figure>
  );
}
